import React, {useEffect, useMemo} from 'react'
import {Redirect, useLocation} from 'react-router-dom'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {MAX_CACHE_AGE_L, STALE_WHILE_REVALIDATE_L} from '../../constants'
import {useCustomerServiceUrl} from '../../hooks/use-customer-service-url'

/**
 * Email Us (NA): iframe src resolved from the `customerServiceUrl` site preference.
 *
 * NA stores a fully-formed URL per brand/locale (brand -> locale -> value JSON),
 * so the resolved value is used directly as the iframe src — no query params are
 * appended here.
 */
const EmailUs = () => {
    const intl = useIntl()
    const {pathname} = useLocation()
    const einstein = useEinstein()
    const {res} = useServerContext()
    const appOrigin = useAppOrigin()
    const customerServiceUrl = useCustomerServiceUrl()

    const iframeSrc = useMemo(() => {
        return typeof customerServiceUrl === 'string' && customerServiceUrl.trim()
            ? customerServiceUrl.trim()
            : null
    }, [customerServiceUrl])

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_L}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_L}`
        )
    }

    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [pathname, einstein])

    useEffect(() => {
        if (res && !iframeSrc) {
            res.status(404)
        }
    }, [res, iframeSrc])

    const pageTitle = intl.formatMessage({
        id: 'email_us.page.title',
        defaultMessage: 'Contact us'
    })
    const iframeTitle = intl.formatMessage({
        id: 'email_us.iframe.title',
        defaultMessage: 'Contact us form'
    })

    const canonicalUrl = `${appOrigin}${pathname}`

    if (!iframeSrc) {
        return <Redirect to="/page-not-found" />
    }

    return (
        <Box data-testid="email-us-page" layerStyle="page" pb={0}>
            <Seo title={pageTitle} canonicalUrl={canonicalUrl}>
                <link rel="canonical" href={canonicalUrl} />
            </Seo>
            <Box maxW="1400px" mx="auto" px={4} position="relative" minH="90vh">
                <iframe
                    loading="lazy"
                    id="email-us-iframe"
                    title={iframeTitle}
                    src={iframeSrc}
                    style={{
                        width: '100%',
                        height: '90vh',
                        border: 0,
                        display: 'block'
                    }}
                />
            </Box>
        </Box>
    )
}

EmailUs.getTemplateName = () => 'email-us'

export default EmailUs
